import { resolveBankAccountRouteName } from '@/helpers/profile/bankAccountRoute.js';

export const ABN_REQUIREMENT_GROUP = 'abn';
export const BILLING_REQUIREMENT_GROUP = 'billing';
export const SUPER_REQUIREMENT_GROUP = 'super';

const BILLING_FIELD_KEYS = new Set(['abn', 'account_name', 'bsb', 'account_number']);
const BANK_FIELD_KEYS = new Set(['account_name', 'bsb', 'account_number']);
const GROUP_KEYS = new Set([ABN_REQUIREMENT_GROUP, BILLING_REQUIREMENT_GROUP, SUPER_REQUIREMENT_GROUP]);

const LABEL_ABN = 'ABN information is missing';
const LABEL_BILLING = 'Billing information is missing';
const LABEL_SUPER = 'Superannuation information is missing';

function isSuperFieldKey(key) {
    return typeof key === 'string' && key.startsWith('super_');
}

function splitBillingFields(fields) {
    const unique = [...new Set(fields)];
    const abnMissing = unique.includes('abn');
    const bankFields = unique.filter((key) => BANK_FIELD_KEYS.has(key));

    return { abnMissing, bankFields };
}

/**
 * Collapse granular missing checks into ABN / billing / super summary rows for the modal.
 *
 * @param {Array<object>} missing
 * @returns {Array<object>}
 */
export function groupMissingRequirements(missing = []) {
    if (!missing?.length) {
        return [];
    }

    const alreadyGrouped = missing.every((item) => GROUP_KEYS.has(item.group) || GROUP_KEYS.has(item.key));
    if (alreadyGrouped) {
        return missing;
    }

    const billingFields = [];
    let hasSuper = false;

    missing.forEach((item) => {
        if (item.group === ABN_REQUIREMENT_GROUP) {
            billingFields.push('abn');
            return;
        }
        if (item.group === BILLING_REQUIREMENT_GROUP) {
            billingFields.push(...(item.fields || []));
            return;
        }
        if (item.group === SUPER_REQUIREMENT_GROUP) {
            hasSuper = true;
            return;
        }
        if (BILLING_FIELD_KEYS.has(item.key)) {
            billingFields.push(item.key);
        } else if (isSuperFieldKey(item.key)) {
            hasSuper = true;
        }
    });

    const { abnMissing, bankFields } = splitBillingFields(billingFields);
    const grouped = [];

    if (abnMissing) {
        grouped.push({
            key: ABN_REQUIREMENT_GROUP,
            group: ABN_REQUIREMENT_GROUP,
            label: LABEL_ABN,
            route: 'profile_edit',
            scrollTo: 'payout-details-abn',
            fields: ['abn'],
            complete: false,
        });
    }

    if (bankFields.length > 0) {
        grouped.push({
            key: BILLING_REQUIREMENT_GROUP,
            group: BILLING_REQUIREMENT_GROUP,
            label: LABEL_BILLING,
            fields: bankFields,
            complete: false,
        });
    }

    if (hasSuper) {
        grouped.push({
            key: SUPER_REQUIREMENT_GROUP,
            group: SUPER_REQUIREMENT_GROUP,
            label: LABEL_SUPER,
            route: 'profile_super',
            complete: false,
        });
    }

    return grouped;
}

export function isAbnGroupItem(item) {
    return item?.group === ABN_REQUIREMENT_GROUP || item?.key === ABN_REQUIREMENT_GROUP;
}

export function isBillingGroupItem(item) {
    return item?.group === BILLING_REQUIREMENT_GROUP || item?.key === BILLING_REQUIREMENT_GROUP;
}

export function isSuperGroupItem(item) {
    return item?.group === SUPER_REQUIREMENT_GROUP || item?.key === SUPER_REQUIREMENT_GROUP;
}

/**
 * @param {object} item - Grouped bank billing requirement
 * @param {object} userData
 * @returns {{ name: string, query?: object }}
 */
export function resolveBillingGroupRoute(item, userData) {
    return { name: resolveBankAccountRouteName(userData) };
}

/**
 * @returns {{ name: string, query: object }}
 */
export function resolveAbnGroupRoute() {
    return { name: 'profile_edit', query: { scroll: 'payout-details-abn' } };
}
